export type FloorLayerKey =
  | 'layout'
  | 'rooms'
  | 'walls'
  | 'devices'
  | 'clients'
  | 'heatmap'
  | 'alarms'
  | 'cameras'
  | 'sensors'
  | 'topology'

export type EditorTool = 'select' | 'wall' | 'room' | 'obstacle' | 'device'

export type HeatmapLayer =
  | 'rssi'
  | 'snr'
  | 'quality'
  | 'utilization'
  | 'interference'
  | 'clientDensity'

export type WallMaterial =
  | 'drywall'
  | 'brick'
  | 'concrete'
  | 'reinforced_concrete'
  | 'glass'
  | 'metal'
  | 'wood'
  | 'custom'

export type FloorDeviceType =
  | 'access_point'
  | 'wireless_controller'
  | 'switch'
  | 'router'
  | 'firewall'
  | 'camera'
  | 'sensor'
  | 'ups'
  | 'pdu'
  | 'iot_gateway'

export type DeviceHealth = 'healthy' | 'warning' | 'critical' | 'offline' | 'maintenance'

export type FloorWall = {
  id: string
  x1: number
  y1: number
  x2: number
  y2: number
  material: WallMaterial
  heightM?: number
  thicknessM?: number
  attenuationDb?: number
}

/** Minimum RSSI target for a room marked as a coverage requirement zone. */
export type FloorCoverageRequirement = {
  minRssiDbm: number
}

export type FloorRoom = {
  id: string
  name: string
  x: number
  y: number
  w: number
  h: number
  /** When set, room is a coverage requirement zone (pass/fail vs min RSSI). */
  coverageRequirement?: FloorCoverageRequirement
}

/** Site/building context stored in floor geometry (no DB migration). */
export type FloorPlanMetadata = {
  buildingName?: string
  floorLevel?: string
  siteLabel?: string
}

export type FloorObstacle = {
  id: string
  type: string
  x: number
  y: number
  /** Footprint width as % of floor width (derived from widthM). */
  w: number
  /** Footprint depth as % of floor height (derived from depthM). */
  h: number
  /** Physical footprint width (m). */
  widthM?: number
  /** Physical footprint depth (m). */
  depthM?: number
  /** Vertical height from floor (m) — RF clears when AP ray passes above this. */
  heightM?: number
  attenuationDb?: number
}

export type FloorDevice = {
  id: string
  type: FloorDeviceType
  name: string
  x: number
  y: number
  deviceId?: string
  mount?: string
  heightM?: number
  orientation?: number
  status?: DeviceHealth
}

export type FloorLayers = Record<FloorLayerKey, boolean>

export type FloorBackgroundCrop = {
  top?: number
  bottom?: number
  left?: number
  right?: number
}

export type FloorGeometry = {
  version: number
  layers: FloorLayers
  heatmapLayer: HeatmapLayer
  bandGHz: number
  backgroundCrop?: FloorBackgroundCrop
  /** Optional site/building labels for multi-floor planning. */
  metadata?: FloorPlanMetadata
  rooms: FloorRoom[]
  walls: FloorWall[]
  obstacles: FloorObstacle[]
  devices: FloorDevice[]
  /** Per-AP channel plan persisted with floor geometry (apId → channel). */
  apChannels?: Record<string, number>
  /** Show ghost markers for suggested AP placements from recommendations. */
  showSuggestedPlacements?: boolean
}

export type ApMountType = 'ceiling' | 'wall' | 'desk' | 'outdoor'

export type PlacedAp = {
  id: string
  ap: string
  x: number
  y: number
  clients?: number
  rssiZone?: string
  band?: string
  txPowerDbm?: number
  /** Primary radio channel for active band (MHz channel number). */
  channel?: number
  deviceId?: string
  /** Install type — affects pattern and effective range in RF simulation. */
  mount?: ApMountType
  mountHeightM?: number
  /** Wall mount boresight (degrees): 0 = east, 90 = south on floor plan. */
  mountOrientationDeg?: number
}

export type ClientRoamingEvent = {
  at: string
  ap: string
  rssi: number
  reason?: 'initial' | 'roam' | 'sticky' | 'disconnect'
}

export type ClientSpeedTestResult = {
  at: string
  downloadMbps: number
  uploadMbps: number
  latencyMs: number
  jitterMs: number
  packetLossPct: number
}

export type FloorClientDot = {
  id: string
  x: number
  y: number
  ap: string
  rssi: number
  ssid?: string
  band?: string
  sticky?: boolean
  /** Short display name, e.g. Laptop-1 */
  label?: string
  /** Demo / controller MAC */
  mac?: string
  deviceType?: string
  ipAddress?: string
  connectedSince?: string
  /** Map position is estimated unless a location service provides coordinates */
  locationEstimated?: boolean
  roamingHistory?: ClientRoamingEvent[]
  lastSpeedTest?: ClientSpeedTestResult | null
}

export type FloorAlarmDot = {
  id: string
  x: number
  y: number
  severity: string
  title: string
  device: string
}

export type FloorRecommendationAction =
  | 'add_ap'
  | 'adjust_power'
  | 'relocate_ap'
  | 'draw_walls'
  | 'draw_obstacles'

export type FloorRecommendationFix = {
  apIds?: string[]
  txByApId?: Record<string, number>
  moveApId?: string
  moveTo?: { x: number; y: number }
  newApAt?: { x: number; y: number }
  perimeterWalls?: boolean
  obstacleType?: string
  obstacleAt?: { x: number; y: number }
  switchTool?: EditorTool
  enableLayer?: FloorLayerKey
}

export type FloorRecommendation = {
  id: string
  priority: string
  title: string
  detail: string
  action: FloorRecommendationAction
  fix?: FloorRecommendationFix
  /** Grouping for RF panel UI. */
  category?: 'coverage' | 'interference' | 'capacity' | 'modeling'
  /** Primary metric driving this recommendation (%). */
  metricPct?: number
  /** Short human-readable fix summary. */
  fixPreview?: string
}

export const DEFAULT_LAYERS: FloorLayers = {
  layout: true,
  rooms: true,
  walls: true,
  devices: true,
  clients: true,
  heatmap: false,
  alarms: true,
  cameras: true,
  sensors: true,
  topology: false,
}

export function defaultGeometry(): FloorGeometry {
  return {
    version: 1,
    layers: { ...DEFAULT_LAYERS },
    heatmapLayer: 'rssi',
    bandGHz: 5,
    rooms: [],
    walls: [],
    obstacles: [],
    devices: [],
    apChannels: {},
  }
}

export function parseGeometry(raw: unknown): FloorGeometry {
  const base = defaultGeometry()
  if (!raw || typeof raw !== 'object') return base
  const g = raw as Partial<FloorGeometry>
  return {
    ...base,
    ...g,
    layers: { ...base.layers, ...(g.layers ?? {}) },
    rooms: Array.isArray(g.rooms) ? g.rooms : [],
    walls: Array.isArray(g.walls) ? g.walls : [],
    obstacles: Array.isArray(g.obstacles) ? g.obstacles : [],
    devices: Array.isArray(g.devices) ? g.devices : [],
    apChannels: g.apChannels && typeof g.apChannels === 'object' ? (g.apChannels as Record<string, number>) : {},
    metadata: g.metadata && typeof g.metadata === 'object' ? (g.metadata as FloorPlanMetadata) : undefined,
    showSuggestedPlacements: typeof g.showSuggestedPlacements === 'boolean' ? g.showSuggestedPlacements : true,
  }
}
